import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { fetchFilteredProviders, setProviderFilter } from "@/features/provider/providerSlice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search, Filter, X, ArrowUp, MapPin, Building, Bookmark, BookmarkCheck, Grid, List, Globe, School, GraduationCap } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { tunisianGovernorates, governorateCities } from '@/data/tunisian-governorates';

import ProviderLogo from "@/components/common/ProviderLogo";

// View types
type ViewType = 'grid' | 'list';

const Providers = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { providers, paginationMeta, loading, error, filter } = useSelector((state: RootState) => state.provider);
  const topRef = useRef<HTMLDivElement>(null);

  // Local state for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('Tunisia');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewType, setViewType] = useState<ViewType>('grid');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [savedProviders, setSavedProviders] = useState<number[]>([]);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  // Popular categories for quick filters
  const popularCategories = [
    { name: "Universities", icon: <GraduationCap className="h-4 w-4" />, value: "university" },
    { name: "Vocational", icon: <School className="h-4 w-4" />, value: "vocational" },
    { name: "Language Schools", icon: <Globe className="h-4 w-4" />, value: "language_school" },
    { name: "Private", icon: <Building className="h-4 w-4" />, value: "private", isOwnership: true },
  ];

  // Scroll to top function
  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Show back to top button when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize providers list on component mount
  useEffect(() => {
    // Reset to default filter and fetch providers
    const initialFilter = {
      search: null,
      type: null,
      ownership: null,
      country: 'Tunisia',
      state: null,
      city: null,
      category: null,
      page: 1,
      limit: 9
    };

    dispatch(setProviderFilter(initialFilter));
    dispatch(fetchFilteredProviders(initialFilter) as any);
  }, [dispatch]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        handleSearch();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle search
  const handleSearch = () => {
    const newFilter = {
      ...filter,
      search: searchTerm || null,
      page: 1
    };
    dispatch(setProviderFilter(newFilter));
    dispatch(fetchFilteredProviders(newFilter) as any);
  };

  // Apply all filters
  const applyFilters = () => {
    const newFilter = {
      search: searchTerm || null,
      type: selectedType === 'all' ? null : selectedType,
      ownership: selectedOwnership === 'all' ? null : selectedOwnership,
      country: selectedCountry || null,
      city: selectedCity === 'all' ? null : selectedCity,
      state: selectedState === 'all' ? null : selectedState,
      category: selectedCategory === 'all' ? null : selectedCategory,
      page: 1,
      limit: filter.limit || 10,
    };

    // Update active filters for display
    const newActiveFilters: string[] = [];
    if (searchTerm) newActiveFilters.push(`Search: ${searchTerm}`);
    if (selectedType !== 'all') newActiveFilters.push(`Type: ${formatProviderType(selectedType)}`);
    if (selectedOwnership !== 'all') newActiveFilters.push(`Ownership: ${selectedOwnership}`);
    if (selectedCity !== 'all') newActiveFilters.push(`City: ${selectedCity}`);
    if (selectedState !== 'all') newActiveFilters.push(`State: ${selectedState}`);
    if (selectedCategory !== 'all') newActiveFilters.push(`Category: ${selectedCategory}`);
    setActiveFilters(newActiveFilters);

    dispatch(setProviderFilter(newFilter));
    dispatch(fetchFilteredProviders(newFilter) as any);
    toast({ title: "Filters Applied", description: "Provider list updated.", duration: 2000 });
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedOwnership('all');
    setSelectedCountry('Tunisia');
    setSelectedCity('all');
    setSelectedCategory('all');
    setSelectedState('all');
    setActiveFilters([]);

    const resetFilter = {
      search: null,
      type: null,
      ownership: null,
      country: 'Tunisia',
      state: null,
      city: null,
      category: null,
      page: 1,
      limit: 10
    };

    dispatch(setProviderFilter(resetFilter));
    dispatch(fetchFilteredProviders(resetFilter) as any);
    toast({ title: "Filters Reset", description: "All filters have been cleared.", duration: 2000 });
  };

  // Toggle save provider
  const toggleSaveProvider = (id: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (savedProviders.includes(id)) {
      setSavedProviders(savedProviders.filter(providerId => providerId !== id));
      toast({ title: "Provider Removed", description: "Provider removed from saved list.", duration: 2000 });
    } else {
      setSavedProviders([...savedProviders, id]);
      toast({ title: "Provider Saved", description: "Provider added to your saved list.", duration: 2000 });
    }
  };

  // Handle quick filter
  const handleQuickFilter = (value: string, isOwnership: boolean = false) => {
    if (isOwnership) {
      setSelectedOwnership(value);
    } else {
      setSelectedType(value);
    }

    const newFilter = {
      ...filter,
      type: !isOwnership ? value : null,
      ownership: isOwnership ? value : null,
      page: 1
    };

    dispatch(setProviderFilter(newFilter));
    dispatch(fetchFilteredProviders(newFilter) as any);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page < 1 || (paginationMeta && page > paginationMeta.totalPages)) {
      return;
    }

    dispatch(setProviderFilter({ ...filter, page }));
    dispatch(fetchFilteredProviders({ ...filter, page }) as any);

    // Scroll to top of the page for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle state change (for cities dropdown)
  const handleStateChange = (value: string) => {
    setSelectedState(value);
    setSelectedCity('all'); // Reset city when state changes
  };

  // Format provider type for display
  const formatProviderType = (type: string) => {
    if (!type) return '';
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50" ref={topRef}>
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-3 rounded-full bg-primary text-white shadow-lg z-50 hover:bg-primary/90 transition-all"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Find the Perfect Training Provider</h1>
            <p className="text-lg md:text-xl text-blue-100 mb-8">Discover top educational institutions and training centers to advance your skills and career</p>

            <div className="relative max-w-2xl mx-auto">
              <div className="flex">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    placeholder="Search for providers, schools, or universities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10 pr-4 py-6 rounded-l-lg rounded-r-none border-0 text-base w-full focus-visible:ring-blue-500"
                  />
                </div>
                <Button
                  onClick={handleSearch}
                  className="rounded-l-none rounded-r-lg bg-amber-500 hover:bg-amber-600 text-white px-6 py-6 text-base font-medium"
                >
                  Search
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {popularCategories.map((category) => (
              <Button
                key={category.value}
                variant={category.isOwnership
                  ? (selectedOwnership === category.value ? "secondary" : "outline")
                  : (selectedType === category.value ? "secondary" : "outline")}
                className={`bg-white/10 hover:bg-white/20 text-white border-white/20 ${category.isOwnership
                  ? (selectedOwnership === category.value ? 'bg-white/30' : '')
                  : (selectedType === category.value ? 'bg-white/30' : '')}`}
                onClick={() => handleQuickFilter(category.value, category.isOwnership)}
              >
                <span className="flex items-center gap-2">
                  {category.icon}
                  {category.name}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* View Controls */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-bold">Browse Providers</h2>
            {paginationMeta && (
              <p className="text-muted-foreground">
                Showing {providers.length} of {paginationMeta.totalItems} providers
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Tabs value={viewType} onValueChange={(value) => setViewType(value as ViewType)} className="mr-2">
              <TabsList className="grid w-[160px] grid-cols-2">
                <TabsTrigger value="grid" className="flex items-center gap-1">
                  <Grid className="h-4 w-4" />
                  Grid
                </TabsTrigger>
                <TabsTrigger value="list" className="flex items-center gap-1">
                  <List className="h-4 w-4" />
                  List
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium">Active Filters:</span>
              {activeFilters.map((filter, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                  {filter}
                  <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => {
                    // Remove this filter
                    const newFilters = [...activeFilters];
                    newFilters.splice(index, 1);
                    setActiveFilters(newFilters);
                    // TODO: Update the actual filter state based on which filter was removed
                  }} />
                </Badge>
              ))}
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs">
                Clear All
              </Button>
            </div>
          </div>
        )}

      {/* Search and Filters */}
      {showFilters && (
        <Card className="mb-8 border border-primary/10 shadow-sm">
          <CardHeader className="pb-3 bg-muted/30">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-primary" />
              Advanced Filters
            </CardTitle>
            <CardDescription>
              Refine your search with specific criteria
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="type" className="flex items-center gap-1">
                  <School className="h-4 w-4 text-primary" />
                  Provider Type
                </Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger id="type" className="bg-white">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="university">University</SelectItem>
                    <SelectItem value="college">College</SelectItem>
                    <SelectItem value="high_school">High School</SelectItem>
                    <SelectItem value="elementary">Elementary</SelectItem>
                    <SelectItem value="middle_school">Middle School</SelectItem>
                    <SelectItem value="vocational">Vocational</SelectItem>
                    <SelectItem value="language_school">Language School</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownership" className="flex items-center gap-1">
                  <Building className="h-4 w-4 text-primary" />
                  Ownership
                </Label>
                <Select value={selectedOwnership} onValueChange={setSelectedOwnership}>
                  <SelectTrigger id="ownership" className="bg-white">
                    <SelectValue placeholder="Select ownership" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="all">All Ownership Types</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="charter">Charter</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country" className="flex items-center gap-1">
                  <Globe className="h-4 w-4 text-primary" />
                  Country
                </Label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger id="country" className="bg-white">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="Tunisia">Tunisia</SelectItem>
                    <SelectItem value="Morocco">Morocco</SelectItem>
                    <SelectItem value="Algeria">Algeria</SelectItem>
                    <SelectItem value="Egypt">Egypt</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="state" className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-primary" />
                  State/Region
                </Label>
                <Select value={selectedState} onValueChange={handleStateChange}>
                  <SelectTrigger id="state" className="bg-white">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="all">All States</SelectItem>
                    {tunisianGovernorates.map(governorate => (
                      <SelectItem key={governorate} value={governorate}>
                        {governorate}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city" className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-primary" />
                  City
                </Label>
                <Select value={selectedCity} onValueChange={setSelectedCity} disabled={selectedState === 'all'}>
                  <SelectTrigger id="city" className={`bg-white ${selectedState === 'all' ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="all">All Cities</SelectItem>
                    {selectedState !== 'all' && governorateCities[selectedState]?.map(city => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category" className="flex items-center gap-1">
                  <GraduationCap className="h-4 w-4 text-primary" />
                  Category
                </Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger id="category" className="bg-white">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Technology">Technology</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Education">Education</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between border-t px-6 py-4 bg-muted/20">
            <Button variant="outline" onClick={resetFilters} className="border-primary/20">
              <X className="mr-2 h-4 w-4" />
              Reset Filters
            </Button>
            <Button onClick={applyFilters} className="bg-primary hover:bg-primary/90">
              Apply Filters
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Providers List */}
      {loading ? (
        <div className="flex flex-col justify-center items-center py-12 gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading providers...</span>
        </div>
      ) : error ? (
        <Card className="bg-destructive/10 border-destructive/20 shadow-sm">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <X className="h-5 w-5" />
              Error Loading Providers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
          </CardContent>
          <CardFooter>
            <Button variant="outline" onClick={resetFilters}>Try Again</Button>
          </CardFooter>
        </Card>
      ) : providers.length === 0 ? (
        <Card className="text-center py-12 border-dashed border-2 border-muted">
          <CardContent className="flex flex-col items-center">
            <div className="bg-muted/50 p-6 rounded-full mb-4">
              <School className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No providers found</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">We couldn't find any providers matching your criteria. Try adjusting your filters or search terms.</p>
            <Button variant="outline" onClick={resetFilters} className="min-w-[150px]">Reset Filters</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {viewType === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {providers.map((provider) => (
                <Link to={`/providers/${provider.id}`} key={provider.id} className="group">
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-all duration-300 border-muted/40 hover:border-primary/30">
                    <div className="relative h-40 bg-muted/20 overflow-hidden">
                      {provider.logoUrl ? (
                        <div className="w-full h-full flex items-center justify-center p-4 bg-white">
                          <ProviderLogo
                            filename={provider.logoUrl}
                            alt={`${provider.name} logo`}
                            className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/20">
                          <span className="text-primary font-bold text-4xl">
                            {provider.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex gap-2">
                        <button
                          onClick={(e) => toggleSaveProvider(provider.id, e)}
                          className="p-1.5 rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-primary transition-colors"
                          aria-label={savedProviders.includes(provider.id) ? "Remove from saved" : "Save provider"}
                        >
                          {savedProviders.includes(provider.id) ?
                            <BookmarkCheck className="h-4 w-4" /> :
                            <Bookmark className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <CardHeader className="pb-2 pt-4">
                      <CardTitle className="text-lg line-clamp-1 group-hover:text-primary transition-colors">{provider.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {provider.city}, {provider.country}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4 min-h-[40px]">
                        {provider.description || "No description available"}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {provider.type && (
                          <Badge variant="outline" className="bg-primary/5 hover:bg-primary/10 transition-colors">
                            {formatProviderType(provider.type)}
                          </Badge>
                        )}
                        {provider.ownership && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-colors">
                            {provider.ownership}
                          </Badge>
                        )}
                        {provider.category && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100 transition-colors">
                            {provider.category}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-4 mb-8">
              {providers.map((provider) => (
                <Link to={`/providers/${provider.id}`} key={provider.id} className="block group">
                  <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 border-muted/40 hover:border-primary/30">
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/4 lg:w-1/5 relative">
                        {provider.logoUrl ? (
                          <div className="w-full h-full min-h-[160px] flex items-center justify-center p-4 bg-white">
                            <ProviderLogo
                              filename={provider.logoUrl}
                              alt={`${provider.name} logo`}
                              className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-full min-h-[160px] flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/20">
                            <span className="text-primary font-bold text-4xl">
                              {provider.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="absolute top-2 right-2">
                          <button
                            onClick={(e) => toggleSaveProvider(provider.id, e)}
                            className="p-1.5 rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-primary transition-colors"
                            aria-label={savedProviders.includes(provider.id) ? "Remove from saved" : "Save provider"}
                          >
                            {savedProviders.includes(provider.id) ?
                              <BookmarkCheck className="h-4 w-4" /> :
                              <Bookmark className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 p-6">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">{provider.name}</h3>
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {provider.city}, {provider.country}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                          {provider.description || "No description available"}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {provider.type && (
                            <Badge variant="outline" className="bg-primary/5 hover:bg-primary/10 transition-colors">
                              {formatProviderType(provider.type)}
                            </Badge>
                          )}
                          {provider.ownership && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-colors">
                              {provider.ownership}
                            </Badge>
                          )}
                          {provider.category && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100 transition-colors">
                              {provider.category}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

      {/* Improved Pagination */}
      {paginationMeta && paginationMeta.totalPages > 1 && (
        <div className="flex justify-center mt-8 mb-12">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => handlePageChange(Math.max(1, paginationMeta.currentPage - 1))}
                  className={paginationMeta.currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-primary/10"}
                />
              </PaginationItem>

              {/* First page */}
              <PaginationItem>
                <PaginationLink
                  onClick={() => handlePageChange(1)}
                  isActive={paginationMeta.currentPage === 1}
                  className="hover:bg-primary/10"
                >
                  1
                </PaginationLink>
              </PaginationItem>

              {/* Ellipsis if needed */}
              {paginationMeta.currentPage > 3 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {/* Pages around current page */}
              {Array.from({ length: paginationMeta.totalPages })
                .map((_, i) => i + 1)
                .filter(page =>
                  page !== 1 &&
                  page !== paginationMeta.totalPages &&
                  page >= paginationMeta.currentPage - 1 &&
                  page <= paginationMeta.currentPage + 1
                )
                .map(page => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      isActive={page === paginationMeta.currentPage}
                      onClick={() => handlePageChange(page)}
                      className="hover:bg-primary/10"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}

              {/* Ellipsis if needed */}
              {paginationMeta.currentPage < paginationMeta.totalPages - 2 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {/* Last page (if more than 1 page) */}
              {paginationMeta.totalPages > 1 && (
                <PaginationItem>
                  <PaginationLink
                    onClick={() => handlePageChange(paginationMeta.totalPages)}
                    isActive={paginationMeta.currentPage === paginationMeta.totalPages}
                    className="hover:bg-primary/10"
                  >
                    {paginationMeta.totalPages}
                  </PaginationLink>
                </PaginationItem>
              )}

              <PaginationItem>
                <PaginationNext
                  onClick={() => handlePageChange(Math.min(paginationMeta.totalPages, paginationMeta.currentPage + 1))}
                  className={paginationMeta.currentPage === paginationMeta.totalPages ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-primary/10"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
    </div>


  );
};

export default Providers;